/**
 * AI Types - Shared types for LLM interactions
 *
 * Used by:
 * - AI Gateway client (Worker)
 * - Chat endpoints (Worker)
 * - Chat UI (Web)
 * - Agent Kit tools (Kits)
 * - Agent Graph (LangGraph runtime)
 */

// ============================================================
// CHAT MESSAGE FORMAT (Vercel AI SDK compatible)
// ============================================================

/**
 * Chat message format compatible with Vercel AI SDK.
 * This is the standard format used throughout the platform for
 * conversations between users and AI agents.
 */
export interface ChatMessage {
  /** Unique identifier for this message */
  id: string;

  /** Who sent this message */
  role: 'user' | 'assistant' | 'system' | 'tool';

  /** The text content of the message */
  content: string;

  /** When the message was created (optional) */
  createdAt?: Date;

  /** Tool invocations made by the assistant (if any) */
  toolInvocations?: ToolInvocation[];

  /** File attachments on this message (if any) */
  attachments?: MessageAttachment[];

  /**
   * Inline images for multimodal messages (base64 encoded).
   * Used for sending images directly without uploading to R2.
   */
  images?: Array<{
    /** MIME type (e.g., 'image/png', 'image/jpeg') */
    mimeType: string;
    /** Base64-encoded image data */
    base64: string;
  }>;
}

/**
 * Represents a tool/function call made by the AI assistant.
 * Tools are how AI agents interact with the system (e.g., search, execute code).
 */
export interface ToolInvocation {
  /** Unique ID for this specific tool call */
  toolCallId: string;

  /** Name of the tool being called (e.g., 'web_search', 'code_executor') */
  toolName: string;

  /** Arguments passed to the tool as key-value pairs */
  args: Record<string, unknown>;

  /** Result returned by the tool (if completed) */
  result?: unknown;

  /** Current state of the tool invocation */
  state: 'pending' | 'result' | 'error';
}

/**
 * File attachment on a message (images, documents, etc.).
 */
export interface MessageAttachment {
  /** Unique identifier for this attachment */
  id: string;

  /** Display name of the file */
  name: string;

  /** Type category for UI rendering */
  type: 'image' | 'document' | 'video' | 'audio';

  /** URL to access the file (R2 storage URL) */
  url: string;

  /** MIME type (e.g., 'image/png', 'application/pdf') */
  mimeType: string;

  /** File size in bytes */
  size: number;
}

// ============================================================
// LLM CONFIGURATION
// ============================================================

/**
 * Configuration options for LLM requests.
 * Controls model behavior, token limits, and system instructions.
 */
export interface LLMConfig {
  /** Model identifier (e.g., 'claude-sonnet-4-5-20250929') */
  model: string;

  /** Temperature for response randomness (0-1, lower = more deterministic) */
  temperature?: number;

  /** Maximum tokens in the response */
  maxTokens?: number;

  /** System prompt to set AI behavior/personality */
  systemPrompt?: string;
}

// ============================================================
// MODEL OPTIONS (for UI model picker)
// ============================================================

/**
 * Describes an available LLM model for the UI model picker.
 */
export interface ModelOption {
  /** Model identifier used in API calls */
  id: string;

  /** Human-readable name for display */
  name: string;

  /** Which provider hosts this model */
  provider: 'anthropic' | 'openai';

  /** Maximum context window size in tokens */
  contextWindow: number;

  /** Optional description of model capabilities */
  description?: string;
}

/**
 * Default models available in the platform.
 * These are the recommended models for most use cases.
 */
export const DEFAULT_MODELS: ModelOption[] = [
  {
    id: 'claude-sonnet-4-5-20250929',
    name: 'Claude Sonnet 4.5',
    provider: 'anthropic',
    contextWindow: 200000,
    description: 'Best balance of speed and intelligence',
  },
  {
    id: 'claude-opus-4-5-20250929',
    name: 'Claude Opus 4.5',
    provider: 'anthropic',
    contextWindow: 200000,
    description: 'Most capable model',
  },
];

// ============================================================
// STREAMING RESPONSE FORMAT
// ============================================================

/**
 * Chunk format for streaming LLM responses.
 * Used to deliver partial responses in real-time as the AI generates text.
 */
export interface StreamChunk {
  /** Type of this chunk */
  type: 'text' | 'tool_call' | 'tool_result' | 'error' | 'done';

  /** Text content (for 'text' type) */
  content?: string;

  /** Tool call details (for 'tool_call' type) */
  toolCall?: {
    id: string;
    name: string;
    args: Record<string, unknown>;
  };

  /** Error message (for 'error' type) */
  error?: string;
}

// ============================================================
// LLM PROVIDER INTERFACE
// ============================================================

/**
 * LLMProvider - Interface for LLM provider abstraction
 *
 * This interface is implemented by the AI Gateway wrapper and used
 * by Agent Kit tools (specialist, planner, researcher).
 *
 * All implementations route through Cloudflare AI Gateway for:
 * - Response caching (reduces costs for repeated queries)
 * - Rate limiting (protects against abuse)
 * - Token usage logging (enables cost tracking)
 */
export interface LLMProvider {
  /**
   * Send a chat completion request and get streaming response.
   *
   * Use this for real-time UI updates as the AI generates text.
   *
   * @param messages - Conversation history
   * @param config - LLM configuration options
   * @returns AsyncIterable of StreamChunk objects
   */
  chat(
    messages: ChatMessage[],
    config: LLMConfig
  ): AsyncIterable<StreamChunk>;

  /**
   * Send a chat completion request and get the complete response.
   *
   * Use this when you need the full response before proceeding
   * (e.g., parsing structured output, making decisions).
   *
   * @param messages - Conversation history
   * @param config - LLM configuration options
   * @returns Promise resolving to the complete response text
   */
  complete(
    messages: ChatMessage[],
    config: LLMConfig
  ): Promise<string>;
}

// ============================================================
// PLANNING TYPES (for Agent Planning State)
// ============================================================

/**
 * PlanStep - Step in an AI-generated plan
 *
 * Used by:
 * - Planner tool in Agent Kit
 * - Planning middleware in Agent Graph
 * - Plan visualization in UI
 *
 * Plans are how Deep Agents track multi-step workflows.
 * Each step can depend on other steps and has a status.
 */
export interface PlanStep {
  /** Unique identifier for this step */
  id: string;

  /** Human-readable description of what this step does */
  description: string;

  /** Current execution status */
  status: 'pending' | 'in_progress' | 'completed' | 'failed';

  /** IDs of steps that must complete before this one can start */
  dependsOn?: string[];

  /** Rough time estimate for this step */
  estimatedDuration?: 'quick' | 'medium' | 'long';
}
